import _defineProperty from "@babel/runtime/helpers/defineProperty";
/*
 *                     NOTICE
 *            COPYRIGHT 2018 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import { MouseMoveTracker } from "abreact/incubator/ui/utils/MouseMoveTracker";
import * as ReactDOM from "react-dom";

/**
 * Helper class used by DataGrid to manage column resizing under user action.
 */
export class ColumnResizeMgr {
  constructor(dataGrid) {
    _defineProperty(this, "onColumnResizeComplete", void 0);
    _defineProperty(this, "dataGrid", void 0);
    // Vertical indicator that moves as user drags
    _defineProperty(this, "trackerOverlay", null);
    _defineProperty(this, "trackerOverlayTop", 0);
    this.dataGrid = dataGrid;
    this.movedHandler = this.movedHandler.bind(this);
    this.releasedHandler = this.releasedHandler.bind(this);
    this.cancelHandler = this.cancelHandler.bind(this);
  }

  /**
   * Start of user action to resize a column.
   * @param {DataGridColumn} column Column to be resized.
   * @param {HTMLElement} divColumnHeaderCell div that wraps the column header cell
   * @param {React.MouseEvent<any>} event Mousedown event that is beginning this operation.
   */
  resizeColumn(column, divColumnHeaderCell, event) {
    // These do not seem to prevent subsequent onClick handlers on column header from firing
    event.preventDefault();
    event.stopPropagation();
    event.nativeEvent.stopImmediatePropagation();
    this.targetColumn = column;

    // Set up bounds
    let gridHtmlElement = ReactDOM.findDOMNode(this.dataGrid);
    this.columnHeaderCellOffsetLeft = divColumnHeaderCell.getBoundingClientRect().left - gridHtmlElement.getBoundingClientRect().left;
    let columnRightX = this.columnHeaderCellOffsetLeft + divColumnHeaderCell.offsetWidth;
    this.trackerOverlayMinX = this.columnHeaderCellOffsetLeft + column.minWidth;
    this.trackerMaxX = Number.MAX_VALUE;
    this.trackerOverlayMaxX = this.dataGrid.props.width;

    // Note -1 is because trackerOverlay is 3px wide
    this.initializeTrackerOverlay(this.trackerOverlayTop, columnRightX - 1, this.dataGrid.props.height);
    let tracker = new MouseMoveTracker(gridHtmlElement);
    tracker.track(event.nativeEvent, this.movedHandler, this.releasedHandler, "col-resize", this.cancelHandler);
  }
  movedHandler(deltaX, deltaY) {
    // ColumnResizeMgr.LOGGER.info(`movedHandler (${deltaX}, ${deltaY})`);

    let actualX = this.trackerOverlayInitialLeft + deltaX;
    let left = Math.max(actualX, this.trackerOverlayMinX);
    left = Math.min(left, this.trackerOverlayMaxX);
    if (this.trackerOverlay) {
      this.trackerOverlay.style.left = left + "px";
    }
    if (actualX > this.trackerOverlayMaxX) {
      // Hide trackerOverlay if we move to right of dataGrid
      // NB: Use visibility: hidden instead of hide() because I saw a temp HSB when transitioning back to show()???
      this.trackerOverlay.style.visibility = "hidden";
    } else {
      this.trackerOverlay.style.visibility = "visible";
    }
  }
  releasedHandler(deltaX, deltaY) {
    let left = Math.max(this.trackerOverlayInitialLeft + deltaX + 1, this.trackerOverlayMinX);
    let colEdge = left;
    left = Math.min(left, this.trackerOverlayMaxX);
    colEdge = Math.min(colEdge, this.trackerMaxX);
    if (this.trackerOverlay) {
      this.trackerOverlay.style.left = left + "px";
    }
    this.removeTrackerOverlay();
    this.dataGrid.setFocusInGrid();
    let newWidth = colEdge - this.columnHeaderCellOffsetLeft;
    // ColumnResizeMgr.LOGGER.debug(`releasedHandler (${deltaX}, ${deltaY}) newSize: ${newWidth}`);

    if (this.onColumnResizeComplete) {
      this.onColumnResizeComplete(this.targetColumn, newWidth);
    }
  }
  cancelHandler() {
    // ColumnResizeMgr.LOGGER.info("cancelHandler");

    this.removeTrackerOverlay();

    // TODO: Restore original column width if we dynamically change size

    this.dataGrid.setFocusInGrid();
  }
  initializeTrackerOverlay(top, left, height) {
    this.removeTrackerOverlay();
    const gridsContainerDivHtmlElement = ReactDOM.findDOMNode(this.dataGrid);
    let doc = gridsContainerDivHtmlElement.ownerDocument;
    this.trackerOverlay = doc.createElement("div");
    this.trackerOverlay.classList.add("ab-data-grid-column-resizer-tracker-overlay");
    this.trackerOverlay.style.height = height + "px";

    // initially position the trackerOverlay over the column edge
    this.trackerOverlayTop = top;
    this.trackerOverlayInitialLeft = left;
    this.trackerOverlay.style.top = top + "px";
    this.trackerOverlay.style.left = left + "px";
    gridsContainerDivHtmlElement.appendChild(this.trackerOverlay);
  }
  removeTrackerOverlay() {
    if (this.trackerOverlay) {
      this.trackerOverlay.remove();
      this.trackerOverlay = null;
    }
  }
}
//# sourceMappingURL=ColumnResizeMgr.js.map
